import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { getPaidListStudentWise } from "../../api/feeApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const feeSubHeadApi = createLookupApi("fee-subheads");

function downloadCsv(filename, feeSubHeads, students) {
  const escape = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
  const header = ["Student ID", "Name", "Roll", ...feeSubHeads.map((s) => s.feeSubHeadName), "Total"];
  const rows = students.map((s) => [
    s.customStudentId,
    s.studentName,
    s.studentRoll,
    ...feeSubHeads.map((sh) => Math.round(s.paidBySubHead[sh.feeSubHeadId] || 0)),
    Math.round(s.total),
  ]);
  const csv = [header, ...rows].map((r) => r.map(escape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Direct port of digicampusbd's `fee-report-yearly-paid-list` ("Paid List
 * Student Wise") — found missing entirely while sweeping Fee Management (see
 * MIGRATION.md): a genuinely different report from the rest of
 * `FeeInvoiceListPage`'s "Fee Invoice List"/"Paid List"/"Invoice Delete", not
 * a variant of it. One row per student in a chosen section+year, one dynamic
 * column per chosen fee sub-head (paid amount + most recent payment date),
 * plus a total — real backend `FeeReportService.viewSectionTotalPaidDetails`,
 * ported to `GET /api/fee-reports/paid-list-student-wise` returning a
 * dynamic {@code feeSubHeadId -> amount} map instead of the old app's own
 * fixed 35-column DTO. CSV export substitutes for the real page's
 * client-side Excel/PDF export (jsPDF/xlsx) — not ported anywhere else in
 * this migration.
 */
export default function FeePaidListStudentWisePage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [feeSubHeads, setFeeSubHeads] = useState([]);
  const [feeSubHeadIds, setFeeSubHeadIds] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    feeSubHeadApi.list().then(setFeeSubHeads);
  }, []);

  const toggleSubHead = (id) => setFeeSubHeadIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSearch = async () => {
    setError("");
    if (!academicYear || !classConfigId || feeSubHeadIds.length === 0) {
      setError("Select academic year, section, and at least one fee sub-head.");
      return;
    }
    setLoading(true);
    try {
      const data = await getPaidListStudentWise(academicYear, classConfigId, feeSubHeadIds);
      setResult(data);
    } catch (err) {
      setError("Failed to load paid list.");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!result) return;
    downloadCsv(`paid-list-student-wise-${academicYear}.csv`, result.feeSubHeads, result.students);
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Paid List Student Wise</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>

      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch} disabled={loading}>
          {loading ? "Loading..." : "Search"}
        </button>
      </div>

      <div style={{ margin: "12px 0" }}>
        <strong>Fee Sub-Heads to include:</strong>{" "}
        {feeSubHeads.map((s) => (
          <label key={s.id} className="checkbox-label" style={{ display: "inline-flex", marginRight: 12 }}>
            <input type="checkbox" checked={feeSubHeadIds.includes(s.id)} onChange={() => toggleSubHead(s.id)} /> {s.name}
          </label>
        ))}
      </div>

      {result && (
        <>
          <div className="form-actions" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="btn-secondary" onClick={handleExport}>
              Export CSV
            </button>
          </div>
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Name</th>
                  <th>Roll</th>
                  {result.feeSubHeads.map((s) => (
                    <th key={s.feeSubHeadId}>{s.feeSubHeadName}</th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {result.students.map((s) => (
                  <tr key={s.identificationId}>
                    <td>{s.customStudentId}</td>
                    <td>{s.studentName}</td>
                    <td>{s.studentRoll}</td>
                    {result.feeSubHeads.map((sh) => {
                      const amount = s.paidBySubHead[sh.feeSubHeadId];
                      const date = s.paymentDateBySubHead[sh.feeSubHeadId];
                      return (
                        <td key={sh.feeSubHeadId}>
                          {amount ? (
                            <>
                              {Math.round(amount)}
                              {date && (
                                <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted, #888)" }}>{date}</div>
                              )}
                            </>
                          ) : (
                            0
                          )}
                        </td>
                      );
                    })}
                    <td>
                      <strong>{Math.round(s.total)}</strong>
                    </td>
                  </tr>
                ))}
                {result.students.length === 0 && (
                  <tr>
                    <td colSpan={4 + result.feeSubHeads.length}>No paid records found for this selection.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </MainLayout>
  );
}
